import React, { ReactNode, useEffect, useState } from 'react';

import Train from '@/assets/icons/Train';
import { SearchPanelList } from '@/components/search/v1/list/SearchPanelList';

export type AutoCompletionListProps = {
  children?: ReactNode;
  data: string[];
  inputValue: string;
  returnSelectedListItem?: (item: string) => void;
  enterPressed?: boolean;
  onEnterPressed?: () => void;
  onListItemIsSelected?: (value: boolean) => void;
  selectedListIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
};
export function AutoCompletionList({
  data,
  inputValue,
  returnSelectedListItem,
  enterPressed,
  onEnterPressed,
  onListItemIsSelected,
  selectedListIndex,
  setSelectedIndex,
  children = undefined,
  ref,
}: AutoCompletionListProps & { ref?: React.Ref<React.ComponentRef<'div'>> }) {
  const [filteredData, setFilteredData] = useState<string[]>(data);
  const [textBeforeDelimiter, setTextBeforeDelimiter] = useState('');
  const [numberAfterDelimiter, setNumberAfterDelimiter] = useState('');

  const [searchPanelIsActive, setSearchPanelIsActive] = useState(true);

  const handleReturnSelectedListItem = (item: string) => {
    setSearchPanelIsActive(false);
    returnSelectedListItem?.(item);
  };

  // filter the data based on the input value
  useEffect(() => {
    const workingQuery = inputValue.replaceAll(';', ',');
    const parts = workingQuery.split(',');

    setSearchPanelIsActive(true);
    if (parts.length > 1) {
      setTextBeforeDelimiter(parts.slice(0, parts.length - 1).join(',')); // Get all parts before the last one
      setNumberAfterDelimiter(parts[parts.length - 1].trim()); // Get the last element
    } else {
      setTextBeforeDelimiter('');
      setNumberAfterDelimiter(inputValue);
    }

    if (numberAfterDelimiter.length > 0) {
      const filteredItems = data?.filter(
        (item) =>
          item.toLowerCase().includes(numberAfterDelimiter.toLowerCase()) &&
          !textBeforeDelimiter.toLowerCase().includes(item.toLowerCase()),
      );

      setFilteredData(filteredItems);
      if (filteredItems?.length === 1 && filteredItems.includes(numberAfterDelimiter) && onEnterPressed) {
        setSearchPanelIsActive(false);
      }
    } else {
      const filteredItems = data.filter((item) => !textBeforeDelimiter.toLowerCase().includes(item.toLowerCase()));

      setFilteredData(filteredItems);
    }
  }, [data, inputValue, numberAfterDelimiter, textBeforeDelimiter]);

  useEffect(() => {
    onListItemIsSelected?.(filteredData[0] === numberAfterDelimiter);
  }, [numberAfterDelimiter, enterPressed, filteredData, onEnterPressed, onListItemIsSelected, returnSelectedListItem]);

  return (
    <SearchPanelList
      selectedListIndex={selectedListIndex}
      setSelectedIndex={setSelectedIndex}
      ref={ref}
      returnSelectedListItem={handleReturnSelectedListItem}
      iconName={Train}
      data={filteredData}
      dataIsAnArrayOfStrings
      searchPanelIsActive={searchPanelIsActive}
      searchValue={inputValue}
      onEnterPressed={onEnterPressed}
      enterPressed={enterPressed}
    >
      {children}
    </SearchPanelList>
  );
}
